import { ArrowUpRight, Trophy } from 'lucide-react';
import { AWARDS, assetUrl, groupSponsors } from '../data';
import { Reveal, SectionHead, useSite } from './shared';

function LogoTile({ sponsor, size = 'h-16' }) {
  const inner = (
    <img
      src={assetUrl(sponsor.logo_url)}
      alt={sponsor.name || 'Partner logo'}
      loading="lazy"
      className="max-h-12 w-auto max-w-[80%] object-contain"
    />
  );
  const cls = `flex ${size} w-full items-center justify-center rounded-xl bg-paper/95 p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(153,204,0,0.35)]`;
  const testid = `partner-logo-${String(sponsor.name || sponsor._key).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  if (sponsor.website) {
    return (
      <a href={sponsor.website} target="_blank" rel="noopener noreferrer" data-testid={testid} className={cls} title={sponsor.name}>
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={testid} className={cls} title={sponsor.name}>
      {inner}
    </div>
  );
}

export default function PartnersAwards() {
  const { sponsors } = useSite();
  const tiers = groupSponsors(sponsors);
  return (
    <>
      <section id="partners" className="tex-dots border-t border-line/10 bg-ink-2/40 py-24 md:py-32">
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <SectionHead
            eyebrow="Partners"
            title={
              <>
                Event <span className="text-lime">Partners (Last Edition)</span>
              </>

            }
          />
          <div className="space-y-10">
            {tiers.map((t, i) => (
              <Reveal key={t.tier} delay={Math.min(i * 0.04, 0.3)}>
                <div data-testid={`partner-tier-${i}`}>
                  <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    <span className="mr-3 text-lime">—</span>
                    {t.tier}
                  </p>
                  <div
                    className={`grid gap-4 ${
                      t.items.length === 1
                        ? 'max-w-xs grid-cols-1'
                        : t.items.length === 2
                        ? 'grid-cols-2 max-w-lg sm:max-w-xl'
                        : t.items.length === 3
                        ? 'grid-cols-2 max-w-3xl sm:grid-cols-3'
                        : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
                    }`}
                  >
                    {t.items.map((s) => (
                      <LogoTile key={s._key} sponsor={s} size={t.items.length === 1 ? 'h-24' : 'h-20'} />
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="awards" className="tex-gold-radial py-24 md:py-32">
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead
              eyebrow="Awards"
              title={
                <>
                  The CXO100 <span className="text-lime">Awards</span> 2027
                </>
              }
              className="mb-0"
            />
            <Reveal delay={0.1}>
              <span
                data-testid="nomination-status-chip"
                className="inline-flex items-center gap-2 rounded-full border border-lime/40 bg-lime/10 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-lime shadow-[0_0_20px_rgba(153,204,0,0.15)]"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-lime animate-pulse" />
                Nomination Starting Soon
              </span>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {AWARDS.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 0.08} className="h-full">
                <article
                  data-testid={`award-card-${a.id}`}
                  className="group relative h-full overflow-hidden rounded-2xl border border-line/10 bg-ink-2/60 p-8 transition-all duration-500 hover:-translate-y-1 hover:border-lime/60 hover:shadow-[0_14px_60px_rgba(153,204,0,0.15)]"
                >
                  <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-lime/10 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
                  <Trophy size={34} strokeWidth={1.4} className="text-lime transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" />
                  <h3 className="mt-6 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight">
                    {a.a} <span className="text-lime">{a.b}</span>
                  </h3>
                  <p className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    <ArrowUpRight size={13} className="text-lime" /> Winners announced at the summit
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
